// Date Range Filter Utilities
// Shared module for date range filtering across the application
// Date boundaries: always use moment.utc([...]) or startOfDayUTC/endOfDayUTC from date-utils.js
// for data-path range computations. moment([...]) without .utc() produces local-midnight timestamps.

const currentYear = new Date().getFullYear();

// Date range options - current year + previous years
export const dateRangeOptions = [
	{ value: 'ALL', label: 'All time' },
	{ value: 'YEAR_0', label: `${currentYear} (Current Year)` },
	{ value: 'YEAR_1', label: `${currentYear - 1}` },
	{ value: 'YEAR_2', label: `${currentYear - 2}` },
	{ value: 'YEAR_3', label: `${currentYear - 3}` },
	{ value: 'CUSTOM', label: 'Custom range' },
];

/**
 * Format a date range as "M/D/YYYY - M/D/YYYY"
 */
export function formatDateRangeLabel(startDate, endDate) {
	// Append T00:00:00 to parse as local time and avoid UTC timezone shift
	const start = new Date(startDate + 'T00:00:00');
	const end = new Date(endDate + 'T00:00:00');
	const formatDate = (d) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
	return `${formatDate(start)} - ${formatDate(end)}`;
}

/**
 * Generate HTML options for date range select
 */
export function generateDateRangeOptionsHtml(selectedValue = 'ALL') {
	return dateRangeOptions.map((opt) => `<option value="${opt.value}"${opt.value === selectedValue ? ' selected' : ''}>${opt.label}</option>`).join('');
}

/**
 * Get Unix timestamps for a date range type
 * Note: Requires moment.js to be available globally
 */
export function getDateRangeTimestamps(rangeType, customStart = null, customEnd = null) {
	const now = moment();
	const year = now.year();

	// Handle year-based options (YEAR_0 = current year, YEAR_1 = last year, etc.)
	if (rangeType && rangeType.startsWith('YEAR_')) {
		const yearsAgo = parseInt(rangeType.split('_')[1], 10);
		const targetYear = year - yearsAgo;
		return {
			start: moment.utc([targetYear, 0, 1]).startOf('day').unix(),
			end: moment.utc([targetYear, 11, 31]).endOf('day').unix(),
		};
	}

	switch (rangeType) {
		case 'CUSTOM':
			if (customStart && customEnd) {
				return {
					start: moment.utc(customStart).startOf('day').unix(),
					end: moment.utc(customEnd).endOf('day').unix(),
				};
			}
			return null;
		case 'ALL':
		default:
			return null; // No filtering
	}
}

/**
 * Filter items by date range
 */
export function filterByDateRange(items, dateRange) {
	if (!dateRange) return items;
	return items.filter((item) => {
		const itemDate = Number(item.date);
		return itemDate >= dateRange.start && itemDate <= dateRange.end;
	});
}

/**
 * Materialize's FormSelect re-init only detects (and destroys) a prior instance via a JS
 * property it stamps on the element (el.M_FormSelect) -- cloning the select to shed stale
 * listeners drops that property, so the next init can't find the old instance to destroy and
 * nests a new .select-wrapper around it instead of replacing it (#6250). Call this before any
 * M.FormSelect.init() on a select that may already be wrapped, cloned or not.
 */
export function unwrapStaleFormSelect(selectElement) {
	if (!selectElement) return;

	const trackedInstance = selectElement.M_FormSelect;
	if (trackedInstance) trackedInstance.destroy();

	// Fall back to flattening by DOM structure for wrapper(s) left by an untracked prior init.
	// resources/select-patch.js (our FormSelect override) nests the select one level deeper,
	// inside a .hide-select div within the wrapper -- so find the wrapper via closest(), not
	// selectElement.parentElement, or a wrapper past that extra layer is never found.
	let staleWrapper = selectElement.closest('.select-wrapper');
	let outermostWrapper = null;
	while (staleWrapper) {
		outermostWrapper = staleWrapper;
		staleWrapper = staleWrapper.parentElement ? staleWrapper.parentElement.closest('.select-wrapper') : null;
	}
	if (outermostWrapper) {
		outermostWrapper.parentElement.replaceChild(selectElement, outermostWrapper);
	}
}

/**
 * Add click handler for CUSTOM option in Materialize dropdown
 */
export function addCustomOptionClickHandler(selectElement, openModalCallback) {
	const wrapper = selectElement.closest('.select-wrapper') || selectElement.parentElement?.querySelector('.select-wrapper');
	if (!wrapper) return;

	const dropdownContent = wrapper.querySelector('.dropdown-content');
	if (dropdownContent) {
		const dropdownItems = dropdownContent.querySelectorAll('li');
		dropdownItems.forEach((item, index) => {
			item.addEventListener('click', function () {
				const optionValue = selectElement.options[index]?.value;
				if (optionValue === 'CUSTOM') {
					setTimeout(() => openModalCallback(), 50);
				}
			});
		});
	}
}

/**
 * Update dropdown to show custom date range in the input field
 * @param {string|HTMLElement} selectElementOrId - Select element or its ID
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @param {function} openModalCallback - Callback to open custom date modal
 */
export function updateDateRangeDropdownLabel(selectElementOrId, startDate, endDate, openModalCallback) {
	const selectElement = typeof selectElementOrId === 'string' ? document.getElementById(selectElementOrId) : selectElementOrId;
	if (!selectElement) return;

	unwrapStaleFormSelect(selectElement);
	M.FormSelect.init(selectElement, { dropdownOptions: { coverTrigger: false } });

	const wrapper = selectElement.closest('.select-wrapper') || selectElement.parentElement?.querySelector('.select-wrapper');
	if (wrapper) {
		wrapper.style.minWidth = '220px';
		const dateRangeText = formatDateRangeLabel(startDate, endDate);

		const displayInput = wrapper.querySelector('input.select-dropdown');
		if (displayInput) {
			displayInput.value = dateRangeText;
			displayInput.dataset.customDateRange = dateRangeText;

			// Restore value after Materialize resets it on click
			displayInput.addEventListener('click', function () {
				const self = this;
				if (self.dataset.customDateRange && selectElement.value === 'CUSTOM') {
					requestAnimationFrame(() => {
						self.value = self.dataset.customDateRange;
					});
				}
			});
		}

		// Re-add click handler for CUSTOM option
		addCustomOptionClickHandler(selectElement, openModalCallback);
	}
}

/**
 * Clear custom date range state
 */
export function clearCustomDateRange(selectElement, modalStartId = 'modalDateRangeStart', modalEndId = 'modalDateRangeEnd') {
	const wrapper = selectElement?.closest('.select-wrapper');
	if (wrapper) {
		const displayInput = wrapper.querySelector('input.select-dropdown');
		if (displayInput) {
			delete displayInput.dataset.customDateRange;
		}
	}

	// Clear the modal date inputs
	const modalStartInput = document.getElementById(modalStartId);
	const modalEndInput = document.getElementById(modalEndId);
	if (modalStartInput) modalStartInput.value = '';
	if (modalEndInput) modalEndInput.value = '';
}

/**
 * Open the custom date range modal
 */
export function openCustomDateRangeModal(modalId = 'customDateRangeModal') {
	const customDateModal = document.getElementById(modalId);
	if (customDateModal) {
		const modalInstance = M.Modal.getInstance(customDateModal) || M.Modal.init(customDateModal);
		modalInstance.open();
	}
}

/**
 * Initialize a date range dropdown with all necessary handlers
 */
export function initializeDateRangeDropdown(selectElement, options = {}) {
	const { onCustomSelect, onOtherSelect, openModalCallback } = options;

	if (!selectElement) return;

	// Populate options
	selectElement.innerHTML = generateDateRangeOptionsHtml();

	// Initialize Materialize
	unwrapStaleFormSelect(selectElement);
	M.FormSelect.init(selectElement, { dropdownOptions: { coverTrigger: false } });

	// Add click handler for CUSTOM option
	addCustomOptionClickHandler(selectElement, openModalCallback || onCustomSelect);

	// Add change handler
	selectElement.addEventListener('change', function () {
		const selectedValue = this.value;
		if (selectedValue === 'CUSTOM') {
			if (onCustomSelect) onCustomSelect();
			else if (openModalCallback) openModalCallback();
		} else {
			clearCustomDateRange(selectElement);
			if (onOtherSelect) onOtherSelect(selectedValue);
		}
	});

	return selectElement;
}
